import { create } from 'zustand';
import { FilterKey } from '../config/filterConfig';

interface GeneralSettings {
  terminal: string;
  maxTabs: number;
  hideWithoutRelated: boolean;
  autoTabOpeningEnabled: boolean;
  autoOpeningWhitelistOnly: boolean;
  motionHighlight: number;
  bmotionHighlight: number;
  maxMCHighlight: number;
  totalPairFeesHighlight: number;
  negativeHighlight: boolean;
}

interface FilterValue {
  from: string;
  to: string;
  enabled: boolean;
}

interface FiltersSettings {
  devPairsCount: FilterValue;
  howManyRelatedShouldMatch: FilterValue;
  relatedMatchingMode: FilterValue;
  motionPercent: FilterValue;
  bmotionPercent: FilterValue;
  maxMCPercent: FilterValue;
  twitterFollowers: FilterValue;
  twitterCommunity: FilterValue;
  twitterVerified: FilterValue;
  twitterFollowing: FilterValue;
  twitterHasAccount: FilterValue;
  openPrice: FilterValue;
  totalPairFees: FilterValue;
  migrationPercent: FilterValue;
  motionGreaterBMotion: FilterValue;
  motionAvgPercent: FilterValue;
  bmotionAvgPercent: FilterValue;
  maxMCAvgPercent: FilterValue;
  protocol: FilterValue;
}

interface DevListsSettings {
  devWhiteList: string;
  devBlackList: string;
}

interface SettingsState {
  general: GeneralSettings;
  filters: FiltersSettings;
  lists: DevListsSettings;
  setGeneral: (key: keyof GeneralSettings, value: string | number | boolean) => void;
  setFilter: (filterKey: FilterKey, part: keyof FilterValue, value: string | boolean) => void;
  setDevList: (key: keyof DevListsSettings, value: string) => void;
  resetFilters: () => void;
  resetGeneral: () => void;
  resetDevLists: () => void;
}

const STORAGE_KEY = 'settings';

const getDefaultFilters = (): FiltersSettings => ({
  devPairsCount: { from: "", to: "", enabled: false },
  howManyRelatedShouldMatch: { from: "1", to: "", enabled: true },
  relatedMatchingMode: { from: "latest", to: "", enabled: true },
  motionPercent: { from: "", to: "", enabled: false },
  bmotionPercent: { from: "", to: "", enabled: false },
  maxMCPercent: { from: "", to: "", enabled: false },
  twitterFollowers: { from: "", to: "", enabled: false },
  twitterCommunity: { from: "", to: "", enabled: false },
  twitterVerified: { from: "", to: "", enabled: false },
  twitterFollowing: { from: "", to: "", enabled: false },
  twitterHasAccount: { from: "", to: "", enabled: false },
  openPrice: { from: "", to: "", enabled: false },
  totalPairFees: { from: "", to: "", enabled: false },
  migrationPercent: { from: "", to: "", enabled: false },
  motionGreaterBMotion: { from: "", to: "", enabled: false },
  motionAvgPercent: { from: "", to: "", enabled: false },
  bmotionAvgPercent: { from: "", to: "", enabled: false },
  maxMCAvgPercent: { from: "", to: "", enabled: false },
  protocol: { from: JSON.stringify(["Pump V1", "Virtual Curve", "Meteora AMM V2", "LaunchLab", "Pump AMM", "Sugar", "Raydium CPMM", "Moonshot", "Raydium V4", "Heaven", "Meteora DLMM", "Boop", "Meteora AMM", "Raydium CLMM", "Orca"]), to: "", enabled: false },
});

const getDefaultGeneral = (): GeneralSettings => ({
  terminal: "axiom",
  maxTabs: 5,
  hideWithoutRelated: true,
  autoTabOpeningEnabled: false,
  autoOpeningWhitelistOnly: false,
  motionHighlight: 20,
  bmotionHighlight: 35,
  maxMCHighlight: 100,
  totalPairFeesHighlight: 0.1,
  negativeHighlight: true,
});

const getDefaultDevLists = (): DevListsSettings => ({
  devWhiteList: "",
  devBlackList: "",
});

const loadFromStorage = (): Partial<SettingsState> | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error);
  }
  return null;
};

const saveToStorage = (state: SettingsState) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving settings to localStorage:', error);
  }
};

const mergeGeneralSettings = (storedGeneral?: Partial<GeneralSettings>): GeneralSettings => {
  const defaults = getDefaultGeneral();
  if (!storedGeneral) {
    return defaults;
  }
  return {
    ...defaults,
    ...storedGeneral,
  };
};

const mergeFilterSettings = (storedFilters?: Partial<FiltersSettings>): FiltersSettings => {
  const defaults = getDefaultFilters();
  if (!storedFilters) {
    return defaults;
  }

  return (Object.keys(defaults) as Array<keyof FiltersSettings>).reduce((acc, key) => {
    acc[key] = {
      ...defaults[key],
      ...(storedFilters[key] || {}),
    };
    return acc;
  }, {} as FiltersSettings);
};

const mergeDevLists = (storedLists?: Partial<DevListsSettings>): DevListsSettings => {
  const defaults = getDefaultDevLists();
  if (!storedLists) {
    return defaults;
  }
  return {
    ...defaults,
    ...storedLists,
  };
};

export const useSettingsStore = create<SettingsState>((set: (fn: (state: SettingsState) => SettingsState) => void) => {
  const stored = loadFromStorage();

  const initialState: SettingsState = {
    general: mergeGeneralSettings(stored?.general),
    filters: mergeFilterSettings(stored?.filters),
    lists: mergeDevLists(stored?.lists),
    setGeneral: (key, value) =>
      set((state: SettingsState) => {
        const newState = {
          ...state,
          general: { ...state.general, [key]: value },
        };
        saveToStorage(newState);
        return newState;
      }),
    setFilter: (filterKey, part, value) =>
      set((state: SettingsState) => {
        const newState = {
          ...state,
          filters: {
            ...state.filters,
            [filterKey]: { ...state.filters[filterKey], [part]: value },
          },
        };
        saveToStorage(newState);
        return newState;
      }),
    setDevList: (key, value) =>
      set((state: SettingsState) => {
        const newState = {
          ...state,
          lists: {
            ...state.lists,
            [key]: value,
          },
        };
        saveToStorage(newState);
        return newState;
      }),
    resetFilters: () =>
      set((state: SettingsState) => {
        const defaultFilters = getDefaultFilters();
        const newState = {
          ...state,
          filters: defaultFilters,
        };
        saveToStorage(newState);
        return newState;
      }),
    resetGeneral: () =>
      set((state: SettingsState) => {
        const defaultGeneral = getDefaultGeneral();
        const newState = {
          ...state,
          general: defaultGeneral,
        };
        saveToStorage(newState);
        return newState;
      }),
    resetDevLists: () =>
      set((state: SettingsState) => {
        const defaultLists = getDefaultDevLists();
        const newState = {
          ...state,
          lists: defaultLists,
        };
        saveToStorage(newState);
        return newState;
      }),
  };

  // Save initial state to storage if it doesn't exist
  if (!stored) {
    saveToStorage(initialState);
  }

  return initialState;
});
